import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaChevronDown, FaChevronRight, FaPlus, FaTrash } from "react-icons/fa";
import {
  createSourceEntityColumnsBulk,
  type CreateSourceEntityColumnInput,
} from "../../services/catalogue.service";
import { SQL_COLUMN_TYPE_SUGGESTIONS } from "../../constants/sqlColumnTypes";

type ColumnDraft = {
  id: number;
  source_entity_column_name: string;
  data_type: string;
  logical_name: string;
  description: string;
  enable_genai: boolean;
  pii: boolean;
  // advanced
  constraint: string;
  data_format: string;
  example_values: string;
  classification: string;
  owner: string;
  notes: string;
};

const ADVANCED_TEXT_FIELDS: Array<{ field: keyof ColumnDraft; label: string }> = [
  { field: "constraint", label: "Constraint" },
  { field: "data_format", label: "Data Format" },
  { field: "example_values", label: "Example Values" },
  { field: "classification", label: "Classification" },
  { field: "owner", label: "Owner" },
  { field: "notes", label: "Notes" },
];

const DEFAULT_DATA_TYPE = "VARCHAR(255)";

let DRAFT_SEQ = 1;
const emptyDraft = (): ColumnDraft => ({
  id: DRAFT_SEQ++,
  source_entity_column_name: "",
  data_type: DEFAULT_DATA_TYPE,
  logical_name: "",
  description: "",
  enable_genai: false,
  pii: false,
  constraint: "",
  data_format: "",
  example_values: "",
  classification: "",
  owner: "",
  notes: "",
});

const TYPE_DATALIST_ID = "add-columns-sql-type-suggestions";

export function AddColumnsModal({
  show,
  onHide,
  dataSourceId,
  sourceEntityId,
  sourceEntityName,
  existingColumnNames,
  onCreated,
}: {
  show: boolean;
  onHide: () => void;
  dataSourceId: number | null;
  sourceEntityId: number | null;
  sourceEntityName?: string;
  // lower-cased existing column names for case-insensitive duplicate detection
  existingColumnNames: string[];
  onCreated: () => void;
}): JSX.Element {
  const [rows, setRows] = useState<ColumnDraft[]>([emptyDraft()]);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Map of row id -> its Name input, so we can focus a newly added row's Name field.
  const nameInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  const pendingFocusRowId = useRef<number | null>(null);

  // After a new row renders, move focus to its Name input for fast keyboard entry.
  useEffect(() => {
    if (pendingFocusRowId.current !== null) {
      const el = nameInputRefs.current.get(pendingFocusRowId.current);
      el?.focus();
      pendingFocusRowId.current = null;
    }
  }, [rows]);

  const existingSet = useMemo(
    () => new Set(existingColumnNames.map((n) => n.trim().toLowerCase())),
    [existingColumnNames]
  );

  const reset = useCallback(() => {
    setRows([emptyDraft()]);
    setExpandedRowId(null);
    setSubmitError("");
    setSaving(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onHide();
  }, [onHide, reset]);

  const updateRow = useCallback((id: number, patch: Partial<ColumnDraft>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const addRow = useCallback(() => {
    const draft = emptyDraft();
    pendingFocusRowId.current = draft.id; // focus its Name input after render
    setRows((prev) => [...prev, draft]);
  }, []);

  const removeRow = useCallback((id: number) => {
    setRows((prev) => (prev.length === 1 ? [emptyDraft()] : prev.filter((r) => r.id !== id)));
  }, []);

  // Per-row validation. Returns a map of rowId -> { name?, type? } error strings.
  const rowErrors = useMemo(() => {
    const errors: Record<number, { name?: string; type?: string }> = {};
    const seenInBatch = new Map<string, number>(); // normalized name -> first row index
    rows.forEach((row, index) => {
      const name = row.source_entity_column_name.trim();
      const type = row.data_type.trim();
      const e: { name?: string; type?: string } = {};
      if (!name) {
        e.name = "Required";
      } else {
        const key = name.toLowerCase();
        if (existingSet.has(key)) {
          e.name = "Already exists on this entity";
        } else if (seenInBatch.has(key)) {
          e.name = `Duplicate of row ${(seenInBatch.get(key) as number) + 1}`;
        } else {
          seenInBatch.set(key, index);
        }
      }
      if (!type) {
        e.type = "Required";
      }
      if (e.name || e.type) {
        errors[row.id] = e;
      }
    });
    return errors;
  }, [rows, existingSet]);

  const hasErrors = Object.keys(rowErrors).length > 0;
  const validRowCount = rows.filter(
    (r) => r.source_entity_column_name.trim() && r.data_type.trim()
  ).length;

  const handleSubmit = useCallback(async () => {
    if (hasErrors || validRowCount === 0) {
      return;
    }
    if (!Number.isFinite(Number(dataSourceId)) || !Number.isFinite(Number(sourceEntityId))) {
      setSubmitError("Missing source entity context.");
      return;
    }
    setSaving(true);
    setSubmitError("");

    const payload: CreateSourceEntityColumnInput[] = rows
      .filter((r) => r.source_entity_column_name.trim() && r.data_type.trim())
      .map((r) => {
        const details: Record<string, any> = {};
        if (r.logical_name.trim()) details.logical_name = r.logical_name.trim();
        if (r.description.trim()) details.description = r.description.trim();
        if (r.constraint.trim()) details.constraint = r.constraint.trim();
        if (r.data_format.trim()) details.data_format = r.data_format.trim();
        if (r.example_values.trim()) details.example_values = r.example_values.trim();
        if (r.classification.trim()) details.classification = r.classification.trim();
        if (r.owner.trim()) details.owner = r.owner.trim();
        if (r.notes.trim()) details.notes = r.notes.trim();
        if (r.enable_genai) details.enable_genai = true;
        if (r.pii) details.pii = true;
        return {
          source_entity_column_name: r.source_entity_column_name.trim(),
          data_type: r.data_type.trim(),
          ...(Object.keys(details).length > 0 ? { source_entity_column_details: details } : {}),
        };
      });

    try {
      await createSourceEntityColumnsBulk({
        dataSourceId: Number(dataSourceId),
        sourceEntityId: Number(sourceEntityId),
        columns: payload,
      });
      onCreated();
      reset();
      onHide();
    } catch (error: any) {
      console.error("Bulk column create failed:", error);
      const conflict = error?.response?.status === 409 || error?.status === 409;
      setSubmitError(
        conflict
          ? "One or more column names already exist on this entity."
          : "Could not create the columns. Please try again."
      );
      setSaving(false);
    }
  }, [hasErrors, validRowCount, dataSourceId, sourceEntityId, rows, onCreated, reset, onHide]);

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Add columns{sourceEntityName ? ` — ${sourceEntityName}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <datalist id={TYPE_DATALIST_ID}>
          {SQL_COLUMN_TYPE_SUGGESTIONS.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>

        <div className="tw-text-sm tw-text-slate-500 tw-mb-3">
          Add one row per column. Name and Data type are required. Use “Add row” (or press Enter on
          the last row) to add more. Extra metadata is under each row’s “More”.
        </div>

        <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
          {/* header — fixed widths on the trailing 3 cols so they align with the row checkboxes */}
          <div className="add-columns-grid__head tw-grid tw-grid-cols-[1.4fr_1.2fr_1.2fr_1.6fr_3rem_3rem_4rem] tw-gap-2 tw-px-1 tw-pb-1 tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
            <div>Name *</div>
            <div>Data type *</div>
            <div>Logical name</div>
            <div>Description</div>
            <div className="tw-text-center">GenAI</div>
            <div className="tw-text-center">PII</div>
            <div />
          </div>

          {rows.map((row, index) => {
            const err = rowErrors[row.id] || {};
            const isExpanded = expandedRowId === row.id;
            const isLastRow = index === rows.length - 1;
            // Enter on any field of the last row appends a new row (fast keyboard entry).
            const handleRowEnter = (e: React.KeyboardEvent) => {
              if (e.key === "Enter" && isLastRow) {
                e.preventDefault();
                addRow();
              }
            };
            return (
              <div key={row.id} className="add-columns-grid__row tw-border-b tw-border-slate-100 tw-py-1">
                <div className="tw-grid tw-grid-cols-[1.4fr_1.2fr_1.2fr_1.6fr_3rem_3rem_4rem] tw-gap-2 tw-items-start">
                  <div>
                    <Form.Control
                      size="sm"
                      ref={(el: HTMLInputElement | null) => {
                        if (el) {
                          nameInputRefs.current.set(row.id, el);
                        } else {
                          nameInputRefs.current.delete(row.id);
                        }
                      }}
                      isInvalid={!!err.name}
                      value={row.source_entity_column_name}
                      placeholder="column_name"
                      onChange={(e) => updateRow(row.id, { source_entity_column_name: e.target.value })}
                      onBlur={() => {
                        // Default Logical Name to the column name (they're usually the same), but only
                        // if the user hasn't typed their own Logical Name.
                        const name = row.source_entity_column_name.trim();
                        if (name && !row.logical_name.trim()) {
                          updateRow(row.id, { logical_name: name });
                        }
                      }}
                      onKeyDown={handleRowEnter}
                    />
                    {err.name ? (
                      <div className="tw-text-[11px] tw-text-rose-600 tw-mt-0.5">{err.name}</div>
                    ) : null}
                  </div>
                  <div>
                    <Form.Control
                      size="sm"
                      list={TYPE_DATALIST_ID}
                      isInvalid={!!err.type}
                      value={row.data_type}
                      placeholder="e.g. VARCHAR(255)"
                      onChange={(e) => updateRow(row.id, { data_type: e.target.value })}
                      onKeyDown={handleRowEnter}
                    />
                    {err.type ? (
                      <div className="tw-text-[11px] tw-text-rose-600 tw-mt-0.5">{err.type}</div>
                    ) : null}
                  </div>
                  <Form.Control
                    size="sm"
                    value={row.logical_name}
                    onChange={(e) => updateRow(row.id, { logical_name: e.target.value })}
                    onKeyDown={handleRowEnter}
                  />
                  <Form.Control
                    size="sm"
                    value={row.description}
                    onChange={(e) => updateRow(row.id, { description: e.target.value })}
                    onKeyDown={handleRowEnter}
                  />
                  <div className="tw-flex tw-justify-center tw-pt-1">
                    <Form.Check
                      type="checkbox"
                      checked={row.enable_genai}
                      onChange={(e) => updateRow(row.id, { enable_genai: e.target.checked })}
                    />
                  </div>
                  <div className="tw-flex tw-justify-center tw-pt-1">
                    <Form.Check
                      type="checkbox"
                      checked={row.pii}
                      onChange={(e) => updateRow(row.id, { pii: e.target.checked })}
                    />
                  </div>
                  <div className="tw-flex tw-gap-1">
                    <button
                      type="button"
                      className="catalogue-tree__icon-action"
                      title={isExpanded ? "Hide extra fields" : "More fields"}
                      onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                    >
                      {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    </button>
                    <button
                      type="button"
                      className="catalogue-tree__icon-action catalogue-tree__icon-action--danger"
                      title="Remove row"
                      onClick={() => removeRow(row.id)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                {isExpanded ? (
                  <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-mt-2 tw-pl-1 tw-pb-2">
                    {ADVANCED_TEXT_FIELDS.map(({ field, label }) => (
                      <Form.Group key={field}>
                        <Form.Label className="tw-text-[11px] tw-text-slate-500 tw-mb-0.5">
                          {label}
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          value={row[field] as string}
                          onChange={(e) => updateRow(row.id, { [field]: e.target.value } as Partial<ColumnDraft>)}
                        />
                      </Form.Group>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="catalogue-tree__action-btn tw-mt-2"
          onClick={addRow}
        >
          <FaPlus className="tw-inline tw-mr-1" /> Add row
        </button>

        {submitError ? (
          <div className="catalogue-register-modal__error tw-mt-3">{submitError}</div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button
          style={{ background: "#293d57", borderColor: "#293d57" }}
          onClick={handleSubmit}
          disabled={saving || hasErrors || validRowCount === 0}
        >
          {saving
            ? "Creating..."
            : `Create ${validRowCount} ${validRowCount === 1 ? "column" : "columns"}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddColumnsModal;
